import { ROUTES } from '@constants/routes';
import type { Me, Permission } from '@lib/api';
import { hasPermission, usesOperations } from '@lib/auth';
import type { IconName } from '@ui';

export interface NavItem { to: string; label: string; icon: IconName; end?: boolean }
export interface NavGroup { id: string; label: string; items: NavItem[] }

/**
 * The navigation rail, grouped the way the work flows: watch the network, run the distribution, the assets
 * (cylinders), the devices that read them, then administration. Every entry needs its read permission (the backend
 * refuses the rest anyway); operators start at their operations home instead of the control room. Empty groups
 * are left out.
 */
export function navGroups(me: Me): NavGroup[] {
  const can = (p: Permission) => hasPermission(me, p);
  const only = (p: Permission, item: NavItem): NavItem[] => (can(p) ? [item] : []);
  const groups: NavGroup[] = [
    {
      id: 'watch',
      label: 'Pantau',
      items: [
        ...(usesOperations(me)
          ? [{ to: ROUTES.operations.home, label: 'Beranda petugas', icon: 'phone' } as NavItem]
          : [
              // a role that lands on Statistik (the server says: a Pengawas) has that page as its Beranda
              ...(me.landing === 'statistics' ? [] : only('overview.read', { to: ROUTES.home, label: 'Beranda', icon: 'home', end: true })),
              ...only('overview.read', { to: ROUTES.controlRoom, label: 'Ruang Kendali', icon: 'map' }),
              ...only('statistics.read', { to: ROUTES.statistics(), label: 'Statistik', icon: 'chart' }),
            ]),
        ...only('alert.read', { to: ROUTES.alerts, label: 'Peringatan', icon: 'bell' }),
      ],
    },
    {
      id: 'distribution',
      label: 'Distribusi',
      items: [
        ...only('shipment.read', { to: ROUTES.shipments(), label: 'Surat Jalan', icon: 'doc' }),
        ...only('location.read', { to: ROUTES.locations, label: 'Titik Distribusi', icon: 'pin' }),
        ...only('hub_application.read', { to: ROUTES.hubApplications(), label: 'Pendaftaran Hub', icon: 'network' }),
      ],
    },
    {
      id: 'program',
      label: 'Program',
      // an operator works its orders and households in operations mode; the overview is for the others
      items: usesOperations(me) ? [] : [...only('order.read', { to: ROUTES.orders(), label: 'Pesanan', icon: 'doc' }), ...only('household.read', { to: ROUTES.households(), label: 'Warga', icon: 'users' })],
    },
    {
      id: 'field',
      label: 'Lapangan',
      items: [...only('order.deliver', { to: ROUTES.deliveries, label: 'Pengantaran', icon: 'truck' }), ...only('hub_application.visit', { to: ROUTES.hubVisits, label: 'Kunjungan', icon: 'calendar' })],
    },
    {
      id: 'assets',
      label: 'Aset',
      items: [...only('cylinder.read', { to: ROUTES.cylinders(), label: 'Tabung', icon: 'cylinder' }), ...(usesOperations(me) ? [] : only('cylinder.read', { to: ROUTES.trace, label: 'Telusur & tarik', icon: 'search' }))],
    },
    {
      id: 'devices',
      label: 'Perangkat',
      items: [...only('gateway.read', { to: ROUTES.gateways, label: 'Reader', icon: 'gateway' }), ...only('ota.manage', { to: ROUTES.updates, label: 'Pembaruan', icon: 'update' })],
    },
    {
      id: 'admin',
      label: 'Admin',
      items: [
        ...only('user.read', { to: ROUTES.users, label: 'Pengguna', icon: 'users' }),
        ...only('program_setting.manage', { to: ROUTES.programSettings, label: 'Pengaturan program', icon: 'key' }),
        ...only('audit.read', { to: ROUTES.audit, label: 'Log aktivitas', icon: 'audit' }),
      ],
    },
  ];
  return groups.filter((g) => g.items.length > 0);
}

/** Screens that are not in the rail but still get a title in the header. */
const OTHER: NavItem[] = [{ to: ROUTES.account, label: 'Akun', icon: 'user' }];

const matches = (pathname: string, i: NavItem) => (i.end ? pathname === i.to : pathname === i.to || pathname.startsWith(`${i.to}/`));

/** Header title of the current screen: its rail entry and group (detail pages take their list's title). */
export function pageTitle(pathname: string, groups: NavGroup[]): { group?: string; title: string } | null {
  for (const g of groups) {
    const hit = g.items.find((i) => matches(pathname, i));
    if (hit) return { group: g.label, title: hit.label };
  }
  const other = OTHER.find((i) => matches(pathname, i));
  return other ? { title: other.label } : null;
}
